import MiniSearch from "minisearch";
import type { MarketplaceEntry, ExtensionType } from "../types/index";
import type { GitHubSource } from "@skills-marketplace/contracts/contract-1-sqs-envelope";
import type { SupportedClient } from "@skills-marketplace/contracts/contract-2-plugin-store";
import { isOfficial } from "./official";
import { usefulnessDisplay } from "./feedback";

/**
 * Catalog orderings. Declared here (rather than beside the dropdown) so the union
 * has one home — it used to be spelled out separately in SearchOptions, sortPlugins
 * and the toolbar, which meant adding an ordering required widening three unions and
 * silently half-worked if you missed one.
 *
 * "relevance" is the odd one out: it is not a comparator but the absence of one,
 * meaning "keep the order the search engine returned". See filterPlugins.
 */
export type SortBy = "relevance" | "mostUsed" | "topRated" | "name" | "updated" | "created" | "official";

/** The orderings sortPlugins can actually compare — everything except "relevance". */
export type ComparatorSortBy = Exclude<SortBy, "relevance">;

export interface SearchOptions {
  query: string;
  supportedClients?: SupportedClient[];
  categories?: string[];
  extensionType?: ExtensionType;
  /** Narrow to entries on the curated Official CAI allowlist. */
  officialOnly?: boolean;
  sortBy?: SortBy;
  sortOrder?: "asc" | "desc";
}

const INDEXED_FIELDS: (keyof MarketplaceEntry)[] = [
  "pluginName", "displayName", "description",
  "orgOrOwner", "repoName", "author",
  "installName", "skillPath",
];

function pluginId(p: MarketplaceEntry): string {
  if (p.installName) return p.installName;
  const base = `${p.source}--${p.orgOrOwner}--${p.repoName}`;
  return p.skillPath ? `${base}--${p.skillPath}` : base;
}

interface SearchIndex {
  mini: MiniSearch<MarketplaceEntry>;
  idToPlugins: Map<string, MarketplaceEntry[]>;
}

function buildIndex(plugins: MarketplaceEntry[]): SearchIndex {
  const ids = new Map<MarketplaceEntry, string>();
  // Dedupe documents by id before indexing. MiniSearch.addAll throws on a
  // duplicate id, and a single malformed catalog record (e.g. two synthesized
  // skills that collide on installName) would otherwise crash search for every
  // plugin. idToPlugins maps every original entry to its id, so searchPlugins
  // returns all entries sharing a matched id — only the redundant index doc is
  // dropped. Defense-in-depth: the catalog should not emit duplicate ids.
  const seen = new Set<string>();
  const docs: (MarketplaceEntry & { _searchId: string })[] = [];
  for (const p of plugins) {
    const id = pluginId(p);
    ids.set(p, id);
    if (seen.has(id)) {
      // Matches the house convention for malformed-catalog signals (see
      // ClientBadge / parseHash): warn once per duplicate so a broken catalog
      // publish leaves a client-side breadcrumb. buildIndex is memoized per
      // catalog array (indexCache), so this does not fire per keystroke.
      console.warn(
        `[search] Duplicate catalog id "${id}" (displayName="${p.displayName}") — ` +
          "dropping redundant index doc. The catalog should not emit duplicate " +
          "installNames; run the reprocess script to investigate.",
      );
      continue;
    }
    seen.add(id);
    docs.push({ ...p, _searchId: id });
  }

  const mini = new MiniSearch<MarketplaceEntry>({
    idField: "_searchId",
    fields: [...INDEXED_FIELDS, "_keywordsText"],
    extractField: (doc, field) => {
      if (field === "_searchId") return (doc as unknown as { _searchId: string })._searchId;
      if (field === "_keywordsText") {
        const kw = (doc as MarketplaceEntry).keywords;
        return kw ? kw.join(" ") : "";
      }
      return (doc as unknown as Record<string, string>)[field] ?? "";
    },
    searchOptions: {
      boost: {
        pluginName: 3, displayName: 3, _keywordsText: 2, description: 1,
      } satisfies Partial<Record<(typeof INDEXED_FIELDS)[number] | "_keywordsText", number>>,
      prefix: (_term, i, terms) => i === terms.length - 1,
      fuzzy: 0.2,
      combineWith: "AND",
    },
  });

  mini.addAll(docs);

  const idToPlugins = new Map<string, MarketplaceEntry[]>();
  for (const p of plugins) {
    const id = ids.get(p)!;
    const bucket = idToPlugins.get(id);
    if (bucket) bucket.push(p);
    else idToPlugins.set(id, [p]);
  }

  return { mini, idToPlugins };
}

const indexCache = new WeakMap<MarketplaceEntry[], SearchIndex>();

function getIndex(plugins: MarketplaceEntry[]): SearchIndex {
  let cached = indexCache.get(plugins);
  if (!cached) {
    cached = buildIndex(plugins);
    indexCache.set(plugins, cached);
  }
  return cached;
}

export function searchPlugins(plugins: MarketplaceEntry[], query: string): MarketplaceEntry[] {
  if (!query.trim()) return plugins;

  const { mini, idToPlugins } = getIndex(plugins);
  const hits = mini.search(query);
  return hits.flatMap((h) => idToPlugins.get(h.id as string) ?? []);
}

/**
 * Filter plugins by source
 */
export function filterBySource(plugins: MarketplaceEntry[], source: GitHubSource): MarketplaceEntry[] {
  return plugins.filter((p) => p.source === source);
}

/**
 * Filter plugins by supported client
 */
export function filterBySupportedClient(
  plugins: MarketplaceEntry[],
  client: SupportedClient,
): MarketplaceEntry[] {
  return plugins.filter((p) => {
    const clients = p.supportedClients && p.supportedClients.length > 0
      ? p.supportedClients
      : ["claude-code"];
    return clients.includes(client);
  });
}

/**
 * Filter plugins by multiple supported clients (OR — must support at least one)
 */
export function filterBySupportedClients(
  plugins: MarketplaceEntry[],
  clients: SupportedClient[],
): MarketplaceEntry[] {
  return plugins.filter((p) => {
    const pluginClients = p.supportedClients && p.supportedClients.length > 0
      ? p.supportedClients
      : ["claude-code"];
    return clients.some((c) => pluginClients.includes(c));
  });
}

/**
 * Filter plugins by skill type
 */
export function filterBySkillType(
  plugins: MarketplaceEntry[],
  skillType: "claude-code" | "claude-cowork"
): MarketplaceEntry[] {
  return plugins.filter((p) => p.skillType === skillType);
}

/**
 * Filter plugins by extension type (plugin vs skill).
 *
 * Strict equality on purpose: an entry whose extensionType is absent/unknown
 * (e.g. a record written before schema 1.3, which predates the field) matches
 * neither "plugin" nor "skill", so it is excluded from those facets but never
 * surfaces under one it doesn't belong to. Such entries stay visible only when
 * no type filter is set.
 */
export function filterByExtensionType(
  plugins: MarketplaceEntry[],
  extensionType: ExtensionType,
): MarketplaceEntry[] {
  return plugins.filter((p) => p.extensionType === extensionType);
}

/**
 * Filter plugins by role category
 */
export function filterByCategory(
  plugins: MarketplaceEntry[],
  category: string,
): MarketplaceEntry[] {
  return plugins.filter((p) =>
    p.categories && p.categories.some(
      (c) => c.toLowerCase() === category.toLowerCase()
    )
  );
}

/**
 * Filter plugins by multiple categories (OR — must match at least one)
 */
export function filterByCategories(
  plugins: MarketplaceEntry[],
  categories: string[],
): MarketplaceEntry[] {
  return plugins.filter((p) =>
    p.categories && p.categories.some(
      (c) => categories.some((cat) => cat.toLowerCase() === c.toLowerCase())
    )
  );
}

/**
 * Filter plugins by keywords (OR — must have at least one)
 */
export function filterByKeywords(
  plugins: MarketplaceEntry[],
  keywords: string[],
): MarketplaceEntry[] {
  return plugins.filter((p) => {
    if (!p.keywords) return false;
    const lower = p.keywords.map((k) => k.toLowerCase());
    return keywords.some((kw) => lower.includes(kw.toLowerCase()));
  });
}

/**
 * Extract unique, sorted categories from a plugin list
 */
export function extractCategories(plugins: MarketplaceEntry[]): string[] {
  const seen = new Map<string, string>();
  for (const plugin of plugins) {
    if (plugin.categories) {
      for (const cat of plugin.categories) {
        const trimmed = cat.trim();
        if (trimmed && !seen.has(trimmed.toLowerCase())) {
          seen.set(trimmed.toLowerCase(), trimmed);
        }
      }
    }
  }
  return Array.from(seen.values()).sort((a, b) =>
    a.toLowerCase().localeCompare(b.toLowerCase())
  );
}

/**
 * Sort plugins
 */
export function sortPlugins(
  plugins: MarketplaceEntry[],
  sortBy: ComparatorSortBy = "name",
  sortOrder: "asc" | "desc" = "asc"
): MarketplaceEntry[] {
  const sorted = [...plugins];
  const multiplier = sortOrder === "asc" ? 1 : -1;

  sorted.sort((a, b) => {
    let aValue: string | number;
    let bValue: string | number;

    switch (sortBy) {
      case "official": {
        // Official entries first, and NOTHING else: within each group the incoming
        // order is preserved (Array.prototype.sort is stable, ES2019+). sortOrder is
        // irrelevant — "Official CAI first" is a fixed ordering.
        //
        // This deliberately has no name tiebreak. "Official first, then by name" reads
        // fine until you sort a set that contains no official entries at all, at which
        // point the tiebreak IS the sort and the control quietly turns into an
        // alphabetical sort. Searching "pr review" against the real catalog does exactly
        // that: none of the matches are allowlisted, so relevance ranking was replaced
        // by alphabetical order for no reason the user could see.
        //
        // Preserving the input order instead means the secondary ordering is always
        // whatever the caller already had, which is the useful answer in both modes:
        // relevance order when a query is active, and alphabetical when browsing
        // (CatalogPage sorts its catalog by name before filtering).
        const aOfficial = isOfficial(a) ? 1 : 0;
        const bOfficial = isOfficial(b) ? 1 : 0;
        return bOfficial - aOfficial;
      }

      case "topRated": {
        // Highest community rating first, lowest last. sortOrder is ignored, as for
        // "official" and "relevance".
        //
        // Ranked on the useful-vote RATIO, not the raw up-vote count: "rating" is what
        // the badge already shows ("N of M found useful"), and a count would rank a
        // 40-of-80 entry above a 12-of-12 one. Ties break toward the larger vote total,
        // since the same ratio backed by more votes is the more confident signal.
        //
        // Only entries the UI actually shows a rating for are ranked. `usefulnessDisplay`
        // is Contract 8's own gate (MIN_VOTES_FOR_RATIO = 5, #620), reused rather than
        // re-implemented so the sort and the badge can never disagree: anything reading
        // "Not enough votes yet" on the card is unranked here. Those sort LAST and are
        // never filtered out — a newly published skill must not vanish from the catalog
        // because nobody has voted on it yet.
        //
        // Ties preserve the incoming order rather than falling back to name, which is
        // the correction #591 made to "official". It matters more here: with no
        // /feedback API deployed, EVERY entry is unrated, so a name tiebreak would turn
        // this into a plain alphabetical re-sort — and would discard relevance ranking
        // outright when a search is active.
        //
        // NOTE: this is a pure usefulness sort. It is deliberately NOT #619's
        // "Recommended" score, which blends log10(activeUsers30d + 1) with this ratio
        // and substitutes a neutral 0.5 below the vote gate. #619 remains unbuilt and
        // owns that formula; see the PR description.
        const aRating = usefulnessDisplay(a.feedback);
        const bRating = usefulnessDisplay(b.feedback);
        if (aRating.kind !== "rated" && bRating.kind !== "rated") return 0;
        if (aRating.kind !== "rated") return 1;
        if (bRating.kind !== "rated") return -1;

        const aRatio = aRating.useful / aRating.total;
        const bRatio = bRating.useful / bRating.total;
        if (aRatio !== bRatio) return bRatio - aRatio;
        return bRating.total - aRating.total;
      }

      case "mostUsed": {
        // Descending by 30-day active users. sortOrder is irrelevant here for the same
        // reason as "official" above: absent adoption means "not measured", never "zero"
        // (see contract-7-adoption.ts), so an unmeasured entry can never legitimately
        // lead the list. Unmeasured entries sort LAST and are never filtered out — a
        // newly published plugin must not vanish from the browse view (#266 AC 6).
        //
        // Explicit undefined checks rather than a `?? -Infinity` sentinel: subtracting
        // two -Infinity values yields NaN, which the spec coerces to 0, so a pair of
        // unmeasured entries would compare "equal" through a NaN path.
        //
        // Ties (both unmeasured, or equal counts) preserve the incoming order rather
        // than tiebreaking by name — the same correction #591 made to "official", and
        // it matters more here. The 2026-08-10 decision was that unmeasured entries
        // "sort by name among themselves, so the tail reads exactly as the Name sort
        // would", and that outcome is unchanged when browsing: CatalogPage sorts its
        // catalog by name before filtering, so preserving that order IS name order.
        //
        // The difference is search. A name tiebreak would turn Most Used into a plain
        // alphabetical re-sort of search results, discarding relevance ranking — precisely
        // the defect #591 fixed in "official". Preserving the input order gives name order
        // while browsing and relevance order while searching, satisfying the decision in
        // the view it was made about.
        //
        // This is a PERMANENT property, not a pre-#264 workaround. With a real artifact
        // published, coverage is 42.4% (contract-7-adoption.ts), so the unmeasured MAJORITY
        // still hits the tie branch — a typical query's matches are mostly unmeasured, and a
        // name tiebreak would still scramble their relevance. Restoring the tiebreak once
        // "the data is in" would reintroduce the bug, not retire a stopgap.
        const aUsers = a.adoption?.activeUsers30d;
        const bUsers = b.adoption?.activeUsers30d;
        if (aUsers === undefined && bUsers === undefined) return 0;
        if (aUsers === undefined) return 1;
        if (bUsers === undefined) return -1;
        return bUsers - aUsers;
      }

      case "name":
        aValue = a.pluginName.toLowerCase();
        bValue = b.pluginName.toLowerCase();
        return aValue < bValue ? -1 * multiplier : aValue > bValue ? 1 * multiplier : 0;

      case "updated":
        aValue = new Date(a.lastUpdatedAt).getTime();
        bValue = new Date(b.lastUpdatedAt).getTime();
        return (aValue - bValue) * multiplier;

      case "created":
        aValue = new Date(a.firstDiscoveredAt).getTime();
        bValue = new Date(b.firstDiscoveredAt).getTime();
        return (aValue - bValue) * multiplier;

      default:
        return 0;
    }
  });

  return sorted;
}

/**
 * Combined search and filter
 */
export function filterPlugins(plugins: MarketplaceEntry[], options: SearchOptions): MarketplaceEntry[] {
  let results = plugins;

  // Search by query
  if (options.query) {
    results = searchPlugins(results, options.query);
  }

  // Filter by supported clients
  if (options.supportedClients && options.supportedClients.length > 0) {
    results = filterBySupportedClients(results, options.supportedClients);
  }

  // Filter by categories
  if (options.categories && options.categories.length > 0) {
    results = filterByCategories(results, options.categories);
  }

  // Filter by extension type (plugin vs skill)
  if (options.extensionType) {
    results = filterByExtensionType(results, options.extensionType);
  }

  // Narrow to Official CAI allowlist
  if (options.officialOnly) {
    results = results.filter(isOfficial);
  }

  // Sort. "relevance" is skipped because it has no comparator: the order to keep is
  // the one searchPlugins already produced. Every other ordering applies whether or
  // not a query is active.
  //
  // This used to be `!options.query.trim()` — sorting was dropped entirely the moment
  // anything was typed, so picking a sort mid-search silently did nothing (#590). The
  // intent was to protect relevance order, which is still protected, but now by asking
  // "is relevance the chosen ordering?" instead of "is a query present?". The two
  // questions only look the same while relevance isn't selectable.
  //
  // With no query, "relevance" leaves the input order untouched — there is nothing to
  // be relevant to. The toolbar only offers it while a query is active for that reason.
  if (options.sortBy && options.sortBy !== "relevance") {
    results = sortPlugins(results, options.sortBy, options.sortOrder);
  }

  return results;
}

/**
 * Debounce utility for search input
 */
export type DebouncedFn<T extends (...args: never[]) => void> = ((...args: Parameters<T>) => void) & {
  cancel: () => void;
};

export function debounce<T extends (...args: never[]) => void>(
  func: T,
  delayMs: number = 300
): DebouncedFn<T> {
  let timeoutId: ReturnType<typeof setTimeout>;

  const debounced = function debounced(...args: Parameters<T>) {
    clearTimeout(timeoutId);
    timeoutId = setTimeout(() => {
      func(...args);
    }, delayMs);
  } as DebouncedFn<T>;

  debounced.cancel = () => {
    clearTimeout(timeoutId);
  };

  return debounced;
}
